import {
  Button,
  Icon,
  Input,
  Shadow,
  SliderModal,
  Steps,
  Text,
  HStack,
  VStack,
} from '@genial/components';
import { BoxRadius, Color, Spacing, Typography } from '@genial/design-tokens';
import { IconographyTypes } from '@genial/icons';
import { Utils, useBreakpointValue } from '@genial/utils';
import { zodResolver } from '@hookform/resolvers/zod';
import { RouteProp, useNavigation, useRoute } from '@react-navigation/native';
import { Pressable } from 'native-base';
import React, { useCallback } from 'react';
import { Control, useForm, useWatch } from 'react-hook-form';
import * as zod from 'zod';

import {
  SignedInNavigationScreenNavigationProp,
  SignedInRootStackParamList,
} from '../../../../../../../../routes/types';
import { Header } from '../../../../../Header';

interface SimulateValueProps {}

const min = 10;
const max = 1000000;
const minValueFormatted = Utils.Mask.Money(min);
const maxValueFormatted = Utils.Mask.Money(max);

const iconPropsFocused = {
  name: 'Close',
  size: 'xxs',
  color: Color['brand.primary.principal'],
  variant: 'rounded',
  backgroundColor: Color['neutral.light'],
} as const;

const iconPropsUnfocused = {
  name: 'EditPen',
  size: 'xxxs',
  color: Color['brand.tertiary.principal'],
} as const;

const createSimulatedValueSchema = zod.object({
  simulatedValue: zod
    .string({ required_error: `Valor mínimo ${minValueFormatted}` })
    .refine((value) => Utils.Unmask.Money(value) >= min, {
      message: `Valor mínimo ${minValueFormatted}`,
    })
    .refine((value) => Utils.Unmask.Money(value) <= max, {
      message: `Valor máximo ${maxValueFormatted}`,
    }),
});

type SimulateValueFormData = zod.infer<typeof createSimulatedValueSchema>;

export const SimulateValue: React.FC<SimulateValueProps> = () => {
  const { params } = useRoute<RouteProp<SignedInRootStackParamList, 'ConsortiumSimulateValue'>>();
  const { canGoBack, goBack, navigate } = useNavigation<SignedInNavigationScreenNavigationProp>();
  const isDesktop = useBreakpointValue({ base: false, lg: true });
  const isMobile = useBreakpointValue({ base: true, sm: false });
  const sliderModalWidth = useBreakpointValue({ base: 1, lg: 0.45 });
  const headerTitle = 'Simulador de consórcio';
  const {
    control,
    resetField,
    formState: { errors },
    handleSubmit,
  } = useForm<SimulateValueFormData>({
    mode: 'all',
    reValidateMode: 'onBlur',
    resolver: zodResolver(createSimulatedValueSchema),
    defaultValues: { simulatedValue: minValueFormatted },
  });

  const currentSimulatedValue = useWatch({
    control,
    name: 'simulatedValue',
  });

  const iconProps =
    currentSimulatedValue !== minValueFormatted ? iconPropsFocused : iconPropsUnfocused;

  const buttonIsDisabled = typeof errors.simulatedValue?.message === 'string';

  const handleGoBack = useCallback(() => {
    if (canGoBack()) {
      goBack();
    }
  }, [canGoBack, goBack]);

  const handleSubmitSimulation = (data: SimulateValueFormData) => {
    const normalizedNumberValue = Utils.Unmask.Money(data.simulatedValue);

    const dataParams = {
      simulatedValue: normalizedNumberValue,
      itemName: params.selectedItem.name,
      categoryIcon: params.selectedCategory.icon,
    };

    navigate('ConsortiumPlans', dataParams);
  };

  return (
    <SliderModal dismissWhenClickOutside onDismiss={handleGoBack} width={sliderModalWidth}>
      <VStack
        p={{ base: Spacing['inline.xxxs'], sm: Spacing['inline.xxs'] }}
        space={Spacing['inline.xxxs']}
        bg="white">
        <HStack alignItems="center" justifyContent="space-between">
          {isDesktop ? (
            <HStack alignItems="center" space={Spacing['inline.nano']}>
              <Pressable onPress={handleGoBack}>
                <Icon
                  testID="input-clear-icon"
                  name="ArrowLeft"
                  size="xxs"
                  color={Color['neutral.black']}
                  borderColor={Color['neutral.black']}
                />
              </Pressable>
              <Text variant="semibold" fontSize={Typography['fontsize.lg']} color="neutral.black">
                {headerTitle}
              </Text>
            </HStack>
          ) : (
            <Header title={headerTitle} onPress={handleGoBack} />
          )}
          {!isMobile && <Steps testId="qa" currentStep={1} totalSteps={2} />}
        </HStack>
        {isMobile && <Steps testId="qa" currentStep={1} totalSteps={2} />}
        <Shadow level={1} borderRadius={BoxRadius.sm}>
          <VStack
            p={Spacing['stack.xxs']}
            space={Spacing['stack.xxxs']}
            borderRadius={BoxRadius.sm}>
            <HStack space={Spacing['inline.nano']} alignItems="center">
              <Icon
                name={params.selectedCategory.icon as IconographyTypes.Option}
                size="sm"
                color={Color['brand.tertiary.principal']}
              />
              <Text variant="semibold" fontSize={Typography['fontsize.xl']} color="neutral.black">
                {params.selectedItem.name}
              </Text>
            </HStack>
            <Text variant="semibold" fontSize={Typography['fontsize.lg']} color="neutral.black">
              Qual o valor estimado?
            </Text>
            <VStack space={Spacing['stack.nano']}>
              <Text variant="regular" fontSize={Typography['fontsize.md']} color="neutral.darkest">
                Valor do consórcio
              </Text>
              <Input
                testID="simulate-value-input"
                onSubmitEditing={handleSubmit(handleSubmitSimulation)}
                keyboardType="numeric"
                name="simulatedValue"
                control={control as unknown as Control}
                autoComplete="off"
                variant="filled"
                mask={Utils.Mask.InputMoney}
                placeholder={`${minValueFormatted}`}
                error={errors.simulatedValue}
                inputRightElement={
                  <Pressable
                    pr={Spacing['inline.nano']}
                    onPress={() => resetField('simulatedValue')}>
                    <Icon testID="qa" {...iconProps} />
                  </Pressable>
                }
              />
            </VStack>
            <VStack
              space={Spacing['stack.nano']}
              py={Spacing['stack.xxxs']}
              px={Spacing['inline.xxs']}
              bg={Utils.ColorToken.WithOpacity('brand.tertiary.lightest', 0.3)}>
              <HStack alignItems="center" justifyContent="space-between">
                <Text
                  variant="regular"
                  fontSize={Typography['fontsize.md']}
                  color="neutral.darkest">
                  Valor mínimo
                </Text>
                <Text
                  variant="regular"
                  fontSize={Typography['fontsize.md']}
                  color="neutral.darkest">
                  {minValueFormatted}
                </Text>
              </HStack>
              <HStack alignItems="center" justifyContent="space-between">
                <Text
                  variant="regular"
                  fontSize={Typography['fontsize.md']}
                  color="neutral.darkest">
                  Valor máximo
                </Text>
                <Text
                  variant="regular"
                  fontSize={Typography['fontsize.md']}
                  color="neutral.darkest">
                  {maxValueFormatted}
                </Text>
              </HStack>
            </VStack>
            <Button isDisabled={buttonIsDisabled} onPress={handleSubmit(handleSubmitSimulation)}>
              SIMULAR
            </Button>
          </VStack>
        </Shadow>
      </VStack>
    </SliderModal>
  );
};
